import { Eye, X } from "lucide-react";
import { useState } from "react";

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

/**
 * A clickable image thumbnail that opens an in-app lightbox viewer (NOT a new
 * tab / download). Hovering dims the image and shows an eye-in-a-circle to signal
 * the preview affordance. Used for every rendered image in Greenlight — comment
 * attachments (Greenlight + ClickUp) and inline description images.
 *
 * `thumbSrc` is what's shown small; `fullSrc` is loaded full-size in the viewer
 * (they're the same for Greenlight-served images, but ClickUp gives a separate
 * thumbnail + full URL).
 */
export function ImageThumb({
  thumbSrc,
  fullSrc,
  alt,
  imgClassName,
  containerClassName,
}: {
  thumbSrc: string;
  fullSrc: string;
  alt: string;
  imgClassName?: string;
  containerClassName?: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={`Preview ${alt || "image"}`}
        className={cn(
          "group relative block w-fit cursor-zoom-in overflow-hidden rounded-md border border-border",
          containerClassName,
        )}
      >
        <img
          src={thumbSrc}
          alt={alt}
          loading="lazy"
          className={cn(
            "max-h-48 max-w-[18rem] object-contain",
            imgClassName,
          )}
        />
        {/* Hover affordance: dim the image + show an eye-in-a-circle. */}
        <span className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/0 transition-colors group-hover:bg-black/25">
          <span className="flex size-9 items-center justify-center rounded-full bg-black/55 text-white opacity-0 shadow-sm transition-opacity group-hover:opacity-100">
            <Eye className="size-4" />
          </span>
        </span>
      </button>

      <ImageViewer
        open={open}
        onClose={() => setOpen(false)}
        src={fullSrc}
        alt={alt}
      />
    </>
  );
}

/** Fullscreen-ish lightbox: dark backdrop, image up to 90vh, Esc / click to close. */
function ImageViewer({
  open,
  onClose,
  src,
  alt,
}: {
  open: boolean;
  onClose: () => void;
  src: string;
  alt: string;
}) {
  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <DialogContent
        showCloseButton={false}
        className="max-w-[95vw] border-0 bg-transparent p-0 shadow-none sm:max-w-[95vw]"
      >
        <DialogTitle className="sr-only">{alt || "Image preview"}</DialogTitle>
        {/* Size this wrapper to the image so the close button hugs its corner —
            anchored to the 95vw DialogContent it floats far right of a narrower
            image. */}
        <div className="relative mx-auto w-fit">
          <DialogClose
            aria-label="Close preview"
            className="absolute right-2 top-2 z-10 flex size-8 cursor-pointer items-center justify-center rounded-full bg-black/55 text-white transition hover:bg-black/75"
          >
            <X className="size-4" />
          </DialogClose>
          <img
            src={src}
            alt={alt}
            onClick={onClose}
            className="block max-h-[90vh] w-auto max-w-full cursor-zoom-out rounded-md object-contain"
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
